/**
 * Created by Ning Jiang on 2024-03-13.
 */

import {WinPresentationController} from "../WinPresentationController";
import {DeadwoodRip} from "../../DeadwoodRip";
import {DeadwoodRipLayers, Layers} from "../../view/DeadwoodRipLayers";
import {TimelineLite} from "gsap";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {CylinderSymbol} from "../../symbol/CylinderSymbol";
import {DeadwoodRipSymbolState} from "../../symbol/DeadwoodRipSymbolConfig";
import {DeadwoodRipReelView} from "../../reels/DeadwoodRipReelView";
import {DeadwoodRipBackground} from "../../view/DeadwoodRipBackground";
import {DeadwoodRipSound} from "../../sound/DeadwoodRipSound";

export class Cylinder extends PIXI.Container {

    private _spine:PIXI.spine.Spine;
    private _above:PIXI.spine.Spine;
    private _under:PIXI.spine.Spine;

    constructor() {
        super();

        this._under = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("cylinderVFX"));
        this._under.autoUpdate = true;
        this.addChild(this._under);

        this._spine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("cylinder"));
        this._spine.autoUpdate = true;
        this.addChild(this._spine);

        this._above = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("cylinderVFX"));
        this._above.autoUpdate = true;
        this.addChild(this._above);
    }

    public load(value:number):TimelineLite {
        const tl = new TimelineLite();
        const aniName = `C_${value}xLand`;
        tl.add(() => {
            this._spine.state.setAnimation(0, aniName, false);
            DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.QUICK_DRAW_LAND});
        });
        tl.add(() => {}, this._spine.spineData.findAnimation(aniName).duration);
        return tl;
    }

    public async shoot(noWin:boolean):Promise<void> {
        const tl = new TimelineLite();
        if(noWin) {
            DeadwoodRip.model.parsedGameData.bulletDataList.forEach((b, i) => {
                const targetSymbol = DeadwoodRip.reels[b.reel].getSymbol(b.row);
                tl.add(targetSymbol.cylinderSplit(b.reel, b.row), 0);
            });
            tl.add(() => {
                this._spine.state.setAnimation(0, "end", false);
                DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.QUICK_DRAW_NO_WIN});
            }, 0);
            tl.add(() => {
                // this._spine.tint = 0x7F7777;
            }, this._spine.spineData.findAnimation("end").duration);
        } else {
            let delay = 0;
            let bulletNum = DeadwoodRip.model.parsedGameData.bulletDataList.length;
            let startAniName = `C_${bulletNum}xStart`;
            tl.add(() => {
                this._spine.state.setAnimation(0, startAniName, false);
                DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.QUICK_DRAW_ACTIVATE});
            });
            delay += this._spine.spineData.findAnimation(startAniName).duration;
            tl.add(() => {}, delay);
            delay += 0.4;
            DeadwoodRip.model.parsedGameData.bulletDataList.forEach((b, i) => {
                const aniName = `C${bulletNum}`;
                const targetSymbol = DeadwoodRip.reels[b.reel].getSymbol(b.row);
                const bulletTl = new TimelineLite();
                bulletTl.add(() => {
                    DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.QUICK_DRAW_FIRE});
                    this._spine.state.setAnimation(0, aniName, false);
                    const aboveEntry = this._above.state.setAnimation(0, "A_above", false);
                    const underEntry = this._under.state.setAnimation(0, "A_underNeath", false);
                    if(i > 0) {
                        aboveEntry.trackTime = 0.387;
                        underEntry.trackTime = 0.387;
                    }
                });
                bulletTl.add(() => {}, this._spine.spineData.findAnimation(aniName).duration);
                bulletTl.add([
                    targetSymbol.shotAtRandomPosition(true, false),
                    targetSymbol.cylinderSplit(b.reel, b.row),
                    () => {
                        DeadwoodRipBackground.shakeStageOnce(0.3);
                        DeadwoodRipSound.playQuickDrawHitSound();
                    }
                ], 0.05);
                tl.add(bulletTl, delay);
                delay += 0.35;
                bulletNum--;
            });
            tl.add(() => {
                this._spine.state.setAnimation(0, "end", false);
            });
            tl.add(() => {
                this._spine.visible = false;
            }, `+=${this._spine.spineData.findAnimation("end").duration}`)
        }

        await Promise.resolve(tl);
    }
}

export class CylinderShotController extends WinPresentationController {

    private _cylinder:Cylinder;
    private _symbol:CylinderSymbol | null;

    constructor() {
        super("CylinderShotController", false);
    }

    protected hasWin():boolean {
        const featureTriggered = DeadwoodRip.model.parsedGameData.bulletDataList && DeadwoodRip.model.parsedGameData.bulletDataList.length > 0;
        return featureTriggered;
    }

    protected async createWinAnimation():Promise<void> {
        DeadwoodRip.playScattersWin = true;
        await this._cylinder.shoot(DeadwoodRip.model.parsedGameData.mode === GameMode.NORMAL && DeadwoodRip.model.parsedGameData.singleWin == 0);
        await this._symbol!.getStateAnimation(
            DeadwoodRipSymbolState.NO_WIN,
            {duration : 0.1, alpha : 1},
            {duration : 0.1, alpha : 0}
        );
    }

    public load(symbol:CylinderSymbol):TimelineLite {
        this._symbol = symbol;
        const position = DeadwoodRipReelView.getGlobalPosition(0, DeadwoodRip.model.parsedGameData.cylinderRow);
        this._cylinder = new Cylinder();
        this._cylinder.position.set(position.x, position.y);
        DeadwoodRipLayers.getLayer(Layers.WIN_PRESENTATIONS)?.addChild(this._cylinder);
        return this._cylinder.load(DeadwoodRip.model.parsedGameData.bulletDataList.length);
    }

    public reset():void {
        this._symbol = null;
        if(this._cylinder) {
            if(this._cylinder.parent) {
                this._cylinder.parent.removeChild(this._cylinder);
            }
            this._cylinder.destroy();
        }
    }
}